import { useState, useMemo, ReactNode } from 'react'
import {
	EuiComboBoxProps,
	EuiComboBoxOptionOption,
} from '@equipmentshare/ds2'

import FormRow, { FormRowProps } from 'components/FormRow'
import useInventoryLocationsSearch from 'hooks/inventory-location-search'
import { Permission, useInventoryLocationsQuery } from '../../graphql'
import { InventoryLocation } from 'inventory-web'
import GroupedComboBox, { OptionsGroup } from 'components/GroupedComboBox/groupedComboBox'

export type InventoryLocationPickerProps = {
	id?: string,
	debounceKey?: string,
	label?: ReactNode,
	onChange: ( id: number, option?: InventoryLocation ) => void,
	selectedInventoryLocation?: InventoryLocation,
	selectedInventoryLocationId?: number,
	formRowProps?: Partial<FormRowProps>,
	isLoading?: boolean,
	permissions?: Permission[],
	/** Whether to fetch all locations upfront (true) or use server-side search (false, default) */
	getAllLocations?: boolean,
	/** Limit the options to warehouses (true) or non-warehouses (false); all locations when omitted */
	isWarehouse?: boolean,
} & Partial<Omit<EuiComboBoxProps<InventoryLocation>, 'onChange'>>

export type FlattenedInventoryLocation = {
	label: string,
	value: InventoryLocation,
	branchName: string,
} & InventoryLocation

export default function InventoryLocationPicker( { label, selectedInventoryLocation, selectedInventoryLocationId, onChange, debounceKey, isLoading, formRowProps, permissions, getAllLocations = false, isWarehouse, ...props }: InventoryLocationPickerProps ) {
	const [ searchString, setSearchString ] = useState( '' )
	const { loading, filteredInventoryLocations } = useInventoryLocationsSearch( searchString, debounceKey, 'no-cache', permissions, undefined, getAllLocations, false, isWarehouse )

	// If ID provided but not object, fetch the location
	const { data: locationData, loading: locationLoading } = useInventoryLocationsQuery( {
		variables: { inventoryLocationIds: selectedInventoryLocationId ? [ String( selectedInventoryLocationId ) ] : [] },
		skip: !selectedInventoryLocationId || !!selectedInventoryLocation,
	} )

	const effectiveSelectedLocation = selectedInventoryLocation ?? locationData?.inventoryLocations?.edges?.[ 0 ]?.node

	// Disable suggestions until user has entered a search term
	const noSuggestions = !getAllLocations && !searchString

	const handleChange = ( inventoryLocation?: InventoryLocation ) => onChange( parseInt( inventoryLocation?.id ?? '0', 10 ) ?? 0, inventoryLocation )

	const flattenedInventoryLocations: FlattenedInventoryLocation[] = useMemo( () => filteredInventoryLocations.map( ( location ) => {
		const inventoryLocation = location as InventoryLocation
		return {
			label: inventoryLocation.name,
			value: inventoryLocation,
			branchName: inventoryLocation.branch.name,
			...inventoryLocation
		}
	} ), [ filteredInventoryLocations ] )

	const sortGroupOptions = ( groups: OptionsGroup<InventoryLocation>[] ) => {
		groups.forEach( ( group ) => {
			const defaultLocationIndex = group.options.findIndex( ( option ) => option.value?.defaultLocation )
			let defaultLocationOption: EuiComboBoxOptionOption<InventoryLocation> | undefined = undefined
			if ( defaultLocationIndex > -1 ) {
				defaultLocationOption = group.options[ defaultLocationIndex ]
				group.options.splice( defaultLocationIndex, 1 )
			}
			group.options.sort( ( a, b ) => ( ( a.label > b.label ) ? 1 : ( a.label < b.label ) ? -1 : 0 ) )
			if ( defaultLocationOption ) {
				group.options.unshift( defaultLocationOption )
			}
		} )
		return groups.sort( ( a, b ) => ( ( a.label > b.label ) ? 1 : ( a.label < b.label ) ? -1 : 0 ) )
	}

	return (
		<FormRow label={label ?? 'InventoryLocations'} {...formRowProps}>
			<GroupedComboBox
				setSelectedOption={handleChange}
				selectedOption={effectiveSelectedLocation}
				isLoading={ isLoading ? isLoading : loading || locationLoading }
				data={flattenedInventoryLocations}
				getName={( location ) => `${location.branch.name} - ${location.name}` }
				getGroupBy={ ( ) => 'branchId' }
				getGroupLabel={ ( location ) => location.branch.name }
				setSearchString={( search: string ) => setSearchString( search )}
				renderOption={ ( option ) => <span>{option.value?.name}</span> }
				sortCallback={sortGroupOptions}
				noSuggestions={noSuggestions}
				{ ...props }
			/>
		</FormRow>
	)
}
